<script setup lang="ts">
    import debounce from 'lodash-es/debounce';
    import { PAGE_SEARCH_KEY } from '@/composables/usePageSearch';
    import { useAppStore } from '@/stores/app';

    const { expand = 'left' } = defineProps<{
        expand?: 'left' | 'center';
    }>();

    const appStore = useAppStore();
    const pageSearch = inject(PAGE_SEARCH_KEY)!;
    const { currentIndex, totalMatches, supportsHighlightAPI, openSearch, closeSearch, updateQuery, goToNext, goToPrev } = pageSearch;

    const inputRef = ref<HTMLInputElement | null>(null);
    const localQuery = ref(appStore.searchQuery || '');
    const isExpanded = computed(() => appStore.searchActive);

    const debouncedUpdate = debounce((query: string) => {
        updateQuery(query);
    }, 300);

    const toggleSearch = () => {
        if (appStore.searchActive) {
            handleClose();
        } else {
            openSearch();
            localQuery.value = appStore.searchQuery || '';
        }
    };

    const onInput = (event: Event) => {
        const target = event.target as HTMLInputElement;
        localQuery.value = target.value;
        debouncedUpdate(target.value);
    };

    const onKeydown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            handleClose();
        } else if (event.key === 'Enter') {
            event.preventDefault();
            if (event.shiftKey) {
                goToPrev();
            } else {
                goToNext();
            }
        }
    };

    const handleClose = () => {
        localQuery.value = '';
        debouncedUpdate.cancel();
        closeSearch();
    };

    watch(isExpanded, (active) => {
        if (active) {
            localQuery.value = appStore.searchQuery || '';
            setTimeout(() => {
                inputRef.value?.focus();
                inputRef.value?.select();
            }, 250);
        }
    });

    watch(
        () => appStore.searchQuery,
        (newQuery) => {
            if (newQuery !== localQuery.value) {
                localQuery.value = newQuery;
            }
        }
    );

    const globalKeyHandler = (event: KeyboardEvent) => {
        if ((event.ctrlKey || event.metaKey) && event.key === 'f') {
            if (appStore.searchActive) {
                event.preventDefault();
                inputRef.value?.focus();
                inputRef.value?.select();
            }
        }
    };

    onMounted(() => {
        window.addEventListener('keydown', globalKeyHandler);
    });

    onBeforeUnmount(() => {
        window.removeEventListener('keydown', globalKeyHandler);
        debouncedUpdate.cancel();
    });

    const counterText = computed(() => {
        if (!localQuery.value || localQuery.value.length < 3) return '';
        if (totalMatches.value === 0) return '0/0';
        return `${currentIndex.value + 1}/${totalMatches.value}`;
    });
</script>

<template>
    <div class="app-expandable-search" :class="{ expanded: isExpanded }">
        <v-btn v-show="!isExpanded" icon variant="text" size="24" color="tbGreen" @click="toggleSearch" data-tooltip="Find in page" class="search-toggle-btn">
            <v-icon :icon="mdiMagnify" size="24" />
        </v-btn>

        <div class="search-panel" :class="`search-panel--${expand}`">
            <div class="search-panel-inner">
                <v-icon :icon="mdiMagnify" size="18" class="search-icon" />

                <input
                    ref="inputRef"
                    type="text"
                    class="search-input"
                    :value="localQuery"
                    @input="onInput"
                    @keydown="onKeydown"
                    placeholder="Find in page..."
                    autocomplete="off"
                    autocapitalize="off"
                    spellcheck="false"
                />

                <span v-if="counterText" class="search-counter" :class="{ 'no-results': totalMatches === 0 }">
                    {{ counterText }}
                </span>

                <div class="search-actions">
                    <v-btn icon variant="text" size="x-small" :disabled="totalMatches === 0" @click="goToPrev" class="search-nav-btn" :ripple="false">
                        <v-icon :icon="mdiChevronUp" size="20" />
                    </v-btn>
                    <v-btn icon variant="text" size="x-small" :disabled="totalMatches === 0" @click="goToNext" class="search-nav-btn" :ripple="false">
                        <v-icon :icon="mdiChevronDown" size="20" />
                    </v-btn>
                    <v-btn icon variant="text" size="x-small" @click="handleClose" class="search-close-btn" :ripple="false">
                        <v-icon :icon="mdiClose" size="18" />
                    </v-btn>
                </div>
            </div>

            <div v-if="isExpanded && !supportsHighlightAPI" class="search-warning">⚠ Search highlighting is not supported in this browser</div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
    .app-expandable-search {
        position: relative;
        display: flex;
        align-items: center;
        flex-shrink: 0;
        z-index: 10;
    }

    .search-toggle-btn {
        transition: opacity 0.15s ease;
    }

    .search-panel {
        background: rgb(var(--v-theme-surface));
        border: 1px solid transparent;
        border-radius: 6px;
        box-shadow: none;
        opacity: 0;
        overflow: hidden;
        pointer-events: none;
        white-space: nowrap;
        transition:
            max-width 0.25s ease,
            opacity 0.2s ease,
            padding 0.25s ease,
            border-color 0.2s ease,
            box-shadow 0.2s ease;
    }

    .search-panel--left {
        position: absolute;
        right: 0;
        top: 50%;
        transform: translateY(-50%);
        max-width: 0;

        .expanded & {
            max-width: min(360px, calc(100vw - 60px));
            opacity: 1;
            pointer-events: auto;
            border-color: rgba(var(--v-border-color), var(--v-border-opacity));
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
        }
    }

    .search-panel--center {
        position: fixed;
        left: 8px;
        right: 8px;
        top: 7px;
        max-width: none;
        z-index: 1002;

        .expanded & {
            opacity: 1;
            pointer-events: auto;
            border-color: rgba(var(--v-border-color), var(--v-border-opacity));
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
        }
    }

    .search-panel-inner {
        display: flex;
        align-items: center;
        gap: 4px;
        height: 34px;
        padding: 0 6px;
        min-width: 300px;
    }

    .search-panel--center .search-panel-inner {
        min-width: 0;
    }

    .search-icon {
        color: rgba(var(--v-theme-on-surface), 0.5);
        flex-shrink: 0;
    }

    .search-input {
        flex: 1;
        min-width: 0;
        border: none;
        outline: none;
        background: transparent;
        font-size: 13px;
        color: rgb(var(--v-theme-on-surface));
        height: 100%;
        padding: 0 4px;

        &::placeholder {
            color: rgba(var(--v-theme-on-surface), 0.4);
        }
    }

    .search-counter {
        font-size: 12px;
        color: rgba(var(--v-theme-on-surface), 0.6);
        white-space: nowrap;
        min-width: 32px;
        text-align: center;
        flex-shrink: 0;
        font-variant-numeric: tabular-nums;

        &.no-results {
            color: rgb(var(--v-theme-tbRed));
        }
    }

    .search-actions {
        display: flex;
        align-items: center;
        gap: 0;
        flex-shrink: 0;
    }

    .search-nav-btn,
    .search-close-btn {
        width: 26px !important;
        height: 26px !important;
        color: rgba(var(--v-theme-on-surface), 0.6) !important;

        &:hover {
            color: rgb(var(--v-theme-on-surface)) !important;
        }

        &:disabled {
            opacity: 0.3;
        }
    }

    .search-warning {
        font-size: 11px;
        color: rgb(var(--v-theme-orange));
        padding: 2px 8px;
        text-align: center;
    }
</style>
